import React from 'react';
import _get from 'lodash/get';
import pluralize from 'pluralize';
import * as Types from 'pages/General/typings';

// Components
import TextField from '@material-ui/core/TextField';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';

// Style
import makeStyles from '@material-ui/core/styles/makeStyles';
const minInputHeight = 65; // Used for flex layout
const arrowIconStyle = { minHeight: minInputHeight, fill: 'rgba(0, 0, 0, 0.55)' };

const TEAM_LEADER_DEFAULT_VALUE = 'Team Leader';

interface Props {
  defaultValue?: string;
  errorMessage: Types.GeneralFormErrors;
  teamMemberUpdateKey: string;
  teamLeaderUpdateKey?: string;
  subtitle: string;
  title: string;
  value: {
    teamMember: string;
    teamLeader?: string;
  };
  onChange: (key: string, value: any) => void;
  renderTeamLeaderField?: boolean;
}
// (Note - Dylan): Component is being used in General Settings and Membership tab -
// teamLeaderUpdateKey is an optional prop since it is only used in the General Settings (and not in the Membership tab)
function Terminology({
  defaultValue = 'Team Member',
  errorMessage,
  teamMemberUpdateKey,
  teamLeaderUpdateKey,
  subtitle,
  title,
  value,
  onChange,
  renderTeamLeaderField,
}: Props) {
  const classes = useStyles();

  const teamMemberValue = _get(value, 'teamMember', defaultValue);
  const teamLeaderValue = _get(value, 'teamLeader', 'Team Leader');

  const handleMemberChange = (e: any) => {
    onChange(teamMemberUpdateKey, e.target.value);
  };

  const handleLeaderChange = (e: any) => {
    if (!teamLeaderUpdateKey) {
      return;
    }

    onChange(teamLeaderUpdateKey, e.target.value);
  };

  return (
    <Card>
      <CardHeader title={title} subheader={subtitle} />
      <Divider />
      <CardContent className={classes.content}>
        <div className={classes.column}>
          <Typography className={classes.subtitle} variant="h6">
            Original Sift Keywords
          </Typography>
          <TextField
            inputProps={{ 'aria-label': defaultValue }}
            value={defaultValue}
            disabled
            className={classes.input}
          />
          {renderTeamLeaderField && (
            <TextField
              inputProps={{ 'aria-label': TEAM_LEADER_DEFAULT_VALUE }}
              value={TEAM_LEADER_DEFAULT_VALUE}
              disabled
              className={classes.input}
            />
          )}
        </div>

        <div className={classes.column}>
          <div className={classes.columnHeader} />
          <KeyboardArrowRightIcon style={arrowIconStyle} />
          {renderTeamLeaderField && <KeyboardArrowRightIcon style={arrowIconStyle} />}
        </div>

        <div className={classes.column}>
          <Typography className={classes.subtitle} variant="h6">
            Converted Keywords
          </Typography>
          <TextField
            error={!!errorMessage.terminology}
            value={teamMemberValue}
            onChange={handleMemberChange}
            className={classes.input}
            inputProps={{ maxLength: 50, 'aria-label': 'plural form' }}
            helperText={
              errorMessage.terminology
                ? errorMessage.terminology
                : 'Plural Form: ' + pluralize(_get(value, 'teamMember', 'Team Member'))
            }
          />
          {renderTeamLeaderField && (
            <TextField
              error={!!errorMessage.leaderTerminology}
              value={teamLeaderValue}
              onChange={handleLeaderChange}
              className={classes.input}
              inputProps={{ maxLength: 50, 'aria-label': 'plural form' }}
              helperText={
                errorMessage.leaderTerminology
                  ? errorMessage.leaderTerminology
                  : 'Plural Form: ' + pluralize(_get(value, 'teamLeader', 'Team Leader'))
              }
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {},
  content: {
    display: 'flex',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
  columnHeader: {
    minHeight: theme.spacing(3),
    fontSize: 14,
    fontWeight: 500,
    color: 'rgba(0, 0, 0, 0.85)',
  },
  input: {
    minHeight: minInputHeight,
  },
  centerText: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  subtitle: {
    marginBottom: theme.spacing(),
  },
}));

export default Terminology;
